import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { AssignmentWidgetVariantProps } from '.';
import { numberSeparator } from '../../../../helpers/formats';
import { getMarket } from '../../../../i18n';
import { getRegistrationNumberIdentifier } from '../../../../shared/registration-number-identifier';
import { Button } from '../../../../styled-components/components/Button';
import { H3 } from '../../../../styled-components/components/Heading';
import InputError from '../../../../styled-components/components/InputError';
import InputLabel from '../../../../styled-components/components/InputLabel';
import { LabelGhost } from '../../../../styled-components/components/InputLabel/wrapper';
import InputText from '../../../../styled-components/components/InputText';
import Loader from '../../../../styled-components/components/Loader';
import { WidgetBox } from '../../../../styled-components/components/WidgetBox';
import { size } from '../../../../styled-components/layout/helpers';

const LayoutWrapper = styled.div.withConfig({ componentId: 'sc-jwpy8s-0' })`
    display: grid;
    align-items: center;
    grid-template-columns: 4fr 6fr;
    gap: ${size(2)};

    ${(props) => props.theme.breakpoint.LtSm} {
        grid-template-columns: 1fr;
    }
`;

const InputWrapperItem = styled.div.withConfig({ componentId: 'sc-jwpy8s-1' })`
    grid-column: span var(--_column-count);
`;

const InputWrapperSubmit = styled.div.withConfig({ componentId: 'sc-jwpy8s-2' })`
    grid-column: span var(--_column-count);
`;

const InputWrapper = styled.div.withConfig({ componentId: 'sc-jwpy8s-3' })`
    --_column-count: 6;

    display: grid;
    gap: ${size(1.5)};
    grid-template-columns: repeat(var(--_column-count), 1fr);
    container: inputWrapper / inline-size;

    ${LabelGhost} {
        display: none;
    }

    @container inputWrapper (min-width: 330px) {
        ${InputWrapperItem} {
            grid-column: span 3;
        }
    }

    @container inputWrapper (min-width: 550px) {
        ${InputWrapperItem} {
            grid-column: span 2;
        }

        ${InputWrapperSubmit} {
            grid-column: span 2;
        }

        ${LabelGhost} {
            display: block;
        }
    }
`;

const WidgetFullWidth = ({
  registrationNumber,
  errorRegistrationNumber,
  odometerReadingValue,
  maxOdometerReadingValue,
  errorOdometerReadingValue,
  disabled,
  loading,
  onBlur,
  onChange,
  onBlurNumber,
  onChangeNumber,
  onProceed,
}: AssignmentWidgetVariantProps) => {
  const { t } = useTranslation(['assignment', 'common', 'glossary']);
  const marketCode = getMarket().marketCode;

  return (
    <WidgetBox $tone="secondary" $size="large">
      <LayoutWrapper>
        <div>
          <H3 as="h2" $margin="small">
            {t('widgetHeading', { ns: 'assignment' })}
          </H3>
          {t('widgetBody', { ns: 'assignment' })}
        </div>
        <InputWrapper>
          <InputWrapperItem>
            <InputLabel htmlFor="assignment-widget-regnr">
              {t('registrationNumber', { ns: 'glossary' })}
            </InputLabel>
            <InputText
              type="text"
              placeholder={t('registrationNumberPlaceholder', { ns: 'common' }) || undefined}
              name="registration_number"
              id="assignment-widget-regnr"
              onBlur={onBlur}
              onChange={onChange}
              value={registrationNumber}
              unit={getRegistrationNumberIdentifier({ market: marketCode })}
            />
            {errorRegistrationNumber && !!registrationNumber && (
              <InputError>
                {t('errorWrongFormatRegistrationNumber', { ns: 'validation' })}
              </InputError>
            )}
          </InputWrapperItem>
          <InputWrapperItem>
            <InputLabel htmlFor="assignment-widget-odometer">
              {t('mileage', { ns: 'glossary' })}
            </InputLabel>
            <InputText
              unit={t('mileageUnit', { ns: 'glossary', count: 2 }).toLowerCase()}
              type="number"
              name="odometerReadingValue"
              id="assignment-widget-odometer"
              placeholder={t('mileage', { ns: 'glossary' }) || undefined}
              value={
                odometerReadingValue !== null && !Number.isNaN(odometerReadingValue)
                  ? odometerReadingValue
                  : ''
              }
              onBlur={onBlurNumber}
              onChange={onChangeNumber}
            />
            {errorOdometerReadingValue && !!odometerReadingValue && (
              <InputError>
                {t('errorValueBetweenTwoNumbers', {
                  ns: 'validation',
                  low: '0',
                  high: numberSeparator(maxOdometerReadingValue),
                })}
              </InputError>
            )}
          </InputWrapperItem>
          <InputWrapperSubmit>
            <LabelGhost />
            <Button
              $tone="primary"
              $fullWidth
              onClick={onProceed}
              title={t('widgetCta', { ns: 'assignment' })}
              disabled={disabled}
            >
              {t('widgetCta', { ns: 'assignment' })}
              {loading && <Loader inlineBlock />}
            </Button>
          </InputWrapperSubmit>
        </InputWrapper>
      </LayoutWrapper>
    </WidgetBox>
  );
};

export default WidgetFullWidth;
